/**
 * Clear stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const clearStickersPack: ToilePack = {
    slug: 'clear-stickers',
    category: 'sticker',
    name: 'Clear stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'fern', label: 'Fern', subject: 'A single fern frond in fine line, no lettering'},
        {key: 'word', label: 'Lettered word', subject: 'The word "BLOOM" in a thin script'},
        {key: 'flower', label: 'Simple flower', subject: 'A simple five-petal flower in flat colour, no lettering'},
    ],
    styles: [
        {key: 'holo', label: 'Holographic', style: 'holographic sticker, iridescent rainbow sheen across the surface, glossy', palette: 'Iridescent silver shifting through pink, cyan and gold', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'puffy', label: 'Puffy', style: 'puffy 3D sticker, pillowed shape with soft rounded edges, glossy vinyl surface', palette: 'Bright candy colours with a soft highlight', framing: 'Each sticker isolated on a plain ground, its raised edge showing'},
        {key: 'clear', label: 'Clear', style: 'clear sticker, the design printed on transparent film with a white underlayer, the edge visible', palette: 'Bright flat colour with a visible clear margin', framing: 'Each sticker isolated on a pale ground with the transparent edge showing'},
    ],
    directions: ['as a leaf instead', 'in one accent colour', 'for a journal cover'],
    directionHint: 'try: as a leaf instead',
    seo: {
        title: 'Clear Stickers: Fern, Lettered Word, Simple Flower',
        metaDescription: 'Nine clear stickers — a line-art fern, a lettered word, a simple flower — with the clear edge showing, in holo, puffy, and clear finishes.',
        h1: 'Clear stickers: fern, lettered word, and flower with a transparent edge',
        tagline: 'Nine transparent stickers, three shapes, three finishes each.',
        intro: 'The transparent sticker: a line-art fern, a lettered word, a simple flower, each drawn so the clear edge shows through around the design. Alongside the clear finish, each also comes in holo and puffy for comparison. Ask for a different line-art motif or word, and the same three finishes carry it.',
        useCases: [
            'a minimalist laptop or water bottle sticker',
            'a journal or planner accent with a see-through look',
            'a wedding or stationery sticker seal',
            'a printable clear sticker sheet',
            'a phone case accent sticker',
        ],
        faq: [
            {q: 'Do these actually print with a transparent background?', a: 'The design is composed so only the line art and lettering show, with a plain background — a genuinely clear film finish needs printing onto real transparent sticker paper.'},
            {q: "What's the difference between clear, holo, and puffy here?", a: 'Clear is the transparent-background look, holo is a rainbow-sheen finish, and puffy is a domed 3D look — all three are included for every shape.'},
            {q: 'Can I get a different word or line-art motif?', a: 'Yes — the direction field takes any word or simple motif, rendered in the same three finishes.'},
            {q: 'What sticker paper should I use for a real clear effect?', a: 'Look for transparent/clear inkjet or laser sticker film, available from most craft and print shops.'},
            {q: 'Are these good for a minimalist aesthetic, not a busy sticker collage?', a: 'Yes — the line-art style is designed to read as clean and understated rather than dense or cluttered.'},
        ],
    },
}
